import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { ExpirationBadge, daysUntil } from "../../components/ExpirationBadge";
import { ProductImage } from "../../components/ProductImage";
import SuggestionsTrigger from "../../components/SuggestionsTrigger";
import { toast } from "sonner";
import { X, Trash2, Boxes } from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import FilterRow from "../../components/system/FilterRow";
import EmptyState from "../../components/system/EmptyState";
import { Select } from "../../components/system/Input";

const FILTERS = [
  { value: "all",       label: "All" },
  { value: "expired",   label: "Expired",   tone: "expired" },
  { value: "urgent",    label: "Urgent",    tone: "urgent" },
  { value: "attention", label: "Soon",      tone: "soon" },
  { value: "fresh",     label: "Fresh",     tone: "fresh" },
];

const REASONS = [
  { key: "expired", label: "Expired" },
  { key: "near_expiration", label: "Near expiration" },
  { key: "damaged", label: "Damaged" },
  { key: "sold_out", label: "Sold out" },
  { key: "other", label: "Other" },
];

export default function AdminInventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrgency = searchParams.get("u") || "all";
  const [batches, setBatches] = useState([]);
  const [depts, setDepts] = useState([]);
  const [filter, setFilter] = useState(initialUrgency);
  const [deptFilter, setDeptFilter] = useState("");

  useEffect(() => {
    const current = searchParams.get("u") || "all";
    if (filter !== current) {
      if (filter === "all") setSearchParams({}, { replace: true });
      else setSearchParams({ u: filter }, { replace: true });
    }
    // eslint-disable-next-line
  }, [filter]);
  const [loading, setLoading] = useState(true);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeForm, setRemoveForm] = useState({ quantity: 1, reason: "expired", note: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.urgency = filter;
      if (deptFilter) params.department_id = deptFilter;
      const [inv, d] = await Promise.all([
        api.get("/inventory", { params }),
        depts.length ? Promise.resolve({ data: depts }) : api.get("/departments"),
      ]);
      setBatches(inv.data);
      if (!depts.length) setDepts(d.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter, deptFilter]);

  const doRemove = async () => {
    if (!removeTarget) return;
    setSubmitting(true);
    try {
      await api.post(`/inventory/${removeTarget.id}/remove`, {
        quantity: Number(removeForm.quantity),
        reason: removeForm.reason,
        note: removeForm.note || null,
      });
      toast.success("Removed");
      setRemoveTarget(null);
      setRemoveForm({ quantity: 1, reason: "expired", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-inventory-page">
      <PageHeader
        eyebrow="Active batches across store"
        title="Inventory"
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className="sm:w-60"
          data-testid="admin-inventory-dept-filter"
        >
          <option value="">All departments</option>
          {depts.map((d) => <option key={d.id} value={d.id}>{deptLabel(d.name)}</option>)}
        </Select>
        <FilterRow
          items={FILTERS}
          value={filter}
          onChange={setFilter}
          className="flex-1"
          data-testid="admin-inventory-filters"
        />
      </div>

      {loading && (
        <Card variant="raised" className="p-4">
          <div className="data-sku">Loading…</div>
        </Card>
      )}

      {!loading && batches.length === 0 && (
        <EmptyState
          icon={<Boxes size={40} strokeWidth={1.5} />}
          title="No items match this filter"
          description="Try clearing the urgency or department filter to see more batches."
          data-testid="admin-empty-inventory"
        />
      )}

      {!loading && batches.length > 0 && (
        <Card variant="raised" className="overflow-hidden">
          {batches.map((b, idx) => {
            const d = daysUntil(b.expiration_date);
            const dept = depts.find((x) => x.id === b.department_id);
            return (
              <div
                key={b.id}
                className={`flex items-center gap-3 p-4 ${idx > 0 ? "border-t border-border/40" : ""}`}
                data-testid={`admin-inventory-row-${b.id}`}
              >
                <ProductImage
                  src={b.product_image_url}
                  alt={b.product_name || ""}
                  size="w-12 h-12"
                  className="rounded-button overflow-hidden shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold truncate">
                    {b.product_name || "—"}
                  </div>
                  <div className="data-sku truncate">
                    {b.product_barcode} · QTY{" "}
                    <span className="text-ink">{b.quantity}</span>
                    {b.batch_code ? ` · BATCH ${b.batch_code}` : ""} ·{" "}
                    {dept ? deptLabel(dept.name) : "—"}
                  </div>
                  <div className="mt-1 text-xs font-mono text-muted-foreground">
                    Expires {b.expiration_date} (
                    {d < 0 ? `${Math.abs(d)}d ago` : `${d}d`})
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 ml-2 shrink-0">
                  <ExpirationBadge urgency={b.urgency} />
                  <SuggestionsTrigger batch={b} />
                  <Button
                    tier="secondary"
                    size="sm"
                    onClick={() => {
                      setRemoveTarget(b);
                      setRemoveForm({
                        quantity: b.quantity,
                        reason: b.urgency === "expired" ? "expired" : "near_expiration",
                        note: "",
                      });
                    }}
                    data-testid={`admin-remove-button-${b.id}`}
                  >
                    <Trash2 size={12} /> Remove
                  </Button>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {removeTarget && (
        <div
          className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-ink/60 px-3"
          data-testid="admin-remove-sheet"
        >
          <Card
            variant="raised"
            className="w-full sm:max-w-md p-6 animate-slide-up"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="data-sku">Remove from stock</div>
                <div className="font-display font-black text-xl">
                  {removeTarget.product_name}
                </div>
                <div className="data-sku">
                  QTY available: {removeTarget.quantity}
                </div>
              </div>
              <button
                onClick={() => setRemoveTarget(null)}
                className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink"
                data-testid="admin-close-remove-sheet"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="data-sku block mb-2">Quantity to remove</label>
                <input
                  type="number"
                  min={1}
                  max={removeTarget.quantity}
                  value={removeForm.quantity}
                  onChange={(e) =>
                    setRemoveForm({ ...removeForm, quantity: e.target.value })
                  }
                  className="w-full h-14 px-4 text-center font-mono text-xl rounded-button bg-paper-2 border border-border/60 shadow-rest focus:shadow-raised focus:outline-none transition-shadow"
                  data-testid="admin-remove-quantity-input"
                />
              </div>
              <div>
                <label className="data-sku block mb-2">Reason</label>
                <div className="grid grid-cols-2 gap-2">
                  {REASONS.map((r) => (
                    <button
                      key={r.key}
                      onClick={() =>
                        setRemoveForm({ ...removeForm, reason: r.key })
                      }
                      className={[
                        "h-11 px-3 rounded-button font-display text-xs uppercase tracking-wide transition-all duration-150 active:scale-[0.98]",
                        removeForm.reason === r.key
                          ? "bg-ink text-white shadow-rest"
                          : "bg-paper-2 text-ink-2 shadow-rest hover:shadow-raised",
                      ].join(" ")}
                      data-testid={`admin-reason-${r.key}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="data-sku block mb-2">Note (optional)</label>
                <textarea
                  value={removeForm.note}
                  onChange={(e) =>
                    setRemoveForm({ ...removeForm, note: e.target.value })
                  }
                  rows={2}
                  className="w-full p-3 rounded-button bg-paper-2 border border-border/60 shadow-rest focus:shadow-raised focus:outline-none text-sm transition-shadow"
                  data-testid="admin-remove-note-input"
                />
              </div>
              <Button
                tier="primary"
                onClick={doRemove}
                disabled={submitting}
                className="w-full bg-status-expired hover:bg-status-expired"
                data-testid="admin-confirm-remove-button"
              >
                {submitting ? "Removing…" : "Confirm removal"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
